export default function ArticlePartThree() {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-3xl font-bold text-gray-900 mb-6">The Quick Close Process: Step by Step</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <img 
            src="https://public.readdy.ai/ai/img_res/7244454d412946262918c04741491826.jpg"
            alt="Quick closing process"
            className="w-full h-96 object-cover object-top rounded-lg mb-6"
          />
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Step 1: Initial Contact and Property Information</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          You reach out to a cash buyer and provide basic information about your property: address, square footage, number of bedrooms and bathrooms, condition, and your desired timeline. Many buyers can provide a preliminary offer range within hours based on this information and public records.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Step 2: Property Walkthrough</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          The buyer schedules a walkthrough to assess the property's condition firsthand. This typically takes 30 to 45 minutes. They'll look at the roof, foundation, plumbing, electrical systems, and overall maintenance. Some buyers can make offers on the spot, while others will follow up within 24 to 48 hours.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Step 3: Receive Written Cash Offer</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          The buyer presents a written offer that includes the purchase price, closing date, and any contingencies. Reputable cash buyers include proof of funds showing they have the money available to complete the purchase. Review the offer carefully and don't hesitate to ask questions or negotiate terms.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Step 4: Accept Offer and Sign Purchase Agreement</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Once you accept the offer, you'll sign a purchase agreement that legally binds both parties to the transaction. This contract specifies the purchase price, closing date, and responsibilities of each party. In Texas, real estate attorneys or title companies typically handle this paperwork.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Step 5: Title Search and Clearance</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          The title company conducts a search to ensure you have clear ownership and there are no liens, judgments, or other claims against the property. If issues are discovered, they must be resolved before closing. This process typically takes 5 to 7 days for cash transactions.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Step 6: Closing Day</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          You meet at the title company to sign the final paperwork. The buyer transfers funds, and you receive your payment, typically via wire transfer or cashier's check. You hand over the keys, and the transaction is complete. The entire closing appointment usually takes less than an hour.
        </p>

        <div className="bg-green-50 rounded-lg p-6 mb-6">
          <h4 className="text-xl font-bold text-gray-900 mb-4">Timeline Summary</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-green-600 mr-3">✓</span>
              <span><strong>Day 1:</strong> Initial contact and property details shared</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-3">✓</span>
              <span><strong>Day 2-3:</strong> Property walkthrough and offer presentation</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-3">✓</span>
              <span><strong>Day 4:</strong> Accept offer and sign purchase agreement</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-3">✓</span>
              <span><strong>Days 5-10:</strong> Title search and clearance</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-3">✓</span>
              <span><strong>Days 11-14:</strong> Closing and payment</span>
            </li>
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Sell Your House for Quick Cash: What You Need to Know</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <img 
            src="https://readdy.ai/api/search-image?query=happy%20homeowner%20receiving%20cash%20payment%20check%20from%20house%20sale%20shaking%20hands%20with%20buyer%20at%20closing%20table%20successful%20quick%20property%20transaction%20professional%20office%20setting&width=1200&height=600&seq=cashsale006&orientation=landscape"
            alt="Successful cash sale completion"
            className="w-full h-96 object-cover object-top rounded-lg mb-6"
          />
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Benefits Beyond Speed</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          While the quick closing timeline is the primary advantage, cash sales offer several other benefits that make them attractive even when speed isn't the only concern.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">No Repairs or Renovations</h4>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Traditional buyers expect move-in ready homes or demand credits for repairs. Cash buyers purchase properties as-is, meaning you don't spend time or money on improvements. This is especially valuable if you lack the capital for repairs or live out of state and can't oversee contractors.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">No Showings or Open Houses</h4>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Listing your home traditionally means keeping it spotless for showings, accommodating buyer schedules, and hosting open houses. With a cash sale, you typically have one walkthrough and you're done. This is particularly beneficial if you're still living in the property or have tenants.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">No Agent Commissions</h4>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Traditional sales involve paying 5% to 6% in real estate agent commissions. On a $200,000 home, that's $10,000 to $12,000. When you sell directly to a cash buyer, you eliminate this cost entirely. While the offer price may be lower than retail market value, your net proceeds are often comparable after accounting for commissions and repair costs.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">Certainty of Closing</h4>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          According to the National Association of Realtors, about 5% of all home sales fall through before closing. With cash buyers who have verified funds, the risk of the deal collapsing is minimal. Once you sign the purchase agreement, you can confidently make plans knowing the sale will close on schedule.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Potential Drawbacks to Consider</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Cash sales aren't perfect for every situation. Understanding the trade-offs helps you make an informed decision.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">Lower Sale Price</h4>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Cash buyers typically offer 10% to 30% below retail market value, depending on the property's condition and local market dynamics. If your house is in excellent condition and you have time to wait, a traditional sale might net you more money even after commissions and closing costs.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">Risk of Lowball Offers</h4>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Some cash buyers prey on desperate sellers with extremely low offers. Always get multiple offers and compare them to your home's estimated value. A fair cash offer should be within a reasonable range of market value minus repair costs and typical selling expenses.
        </p>

        <div className="bg-yellow-50 rounded-lg p-6 mb-6 border-l-4 border-yellow-500">
          <h4 className="text-xl font-bold text-gray-900 mb-4">⚠️ Red Flags to Watch For</h4>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="text-yellow-600 mr-3">•</span>
              <span>Buyer refuses to provide proof of funds or business credentials</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-3">•</span>
              <span>Pressure tactics or demands to sign immediately without time to review</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-3">•</span>
              <span>Requests for upfront fees or payments before closing</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-3">•</span>
              <span>Unwillingness to use a reputable title company or attorney</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-3">•</span>
              <span>Offers that seem too good to be true compared to market value</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}